import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { PricingTypeFilters, PricingTypes } from '../models';
import * as pricingTypesService from '../services/pricing-types.service';

export const usePricingTypes = (
  defaultFilters: Partial<PricingTypeFilters & { searchParams: { ids: string[] } }> = {},
) => {
  const { searchParams, ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<PricingTypeFilters>>(otherDefaultFilters);

  const cacheData = pricingTypesService.getAll();

  const updateFilters = useCallback((newFilters: Partial<PricingTypeFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const { data, isLoading, isError } = useQuery(pricingTypesService.pricingTypesQueryOptions(filters));

  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length || !data?.map) {
      return [];
    }
    return searchParams.ids.filter((id) => !data?.map[id] && !cacheData?.map[id]);
  }, [searchParams?.ids, data?.map, cacheData?.map]);

  const {
    data: preloadData,
    isLoading: isPreloadLoading,
    isError: isPreloadError,
  } = useQuery(
    pricingTypesService.pricingTypesQueryOptions(
      { search: { codeId: missingIds.join(',') } },
      { enabled: missingIds.length > 0 },
    ),
  );

  const mergedMap: PricingTypes['map'] = {
    ...(data?.map ?? {}),
    ...(cacheData?.map ?? {}),
    ...(preloadData?.map ?? {}),
  };

  return {
    data: {
      list: Object.values(mergedMap),
      map: mergedMap,
    },
    isError: isError || isPreloadError,
    isLoading: isLoading || isPreloadLoading,
    updateFilters,
  };
};
